import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AvatarGroup,
  AvatarGroupItem,
  Button,
  Checkbox,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  FluentProvider,
  Input,
  Menu,
  MenuItem,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Option,
  Persona,
  SearchBox,
  Select,
  Spinner,
  Tab,
  TabList,
  Textarea,
  webLightTheme,
} from "@fluentui/react-components";
import type { SelectTabData, SelectTabEvent } from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import {
  Add20Regular,
  Flash20Regular,
  ArrowMove20Regular,
  Settings20Regular,
  QuestionCircle20Regular,
  Sparkle20Filled,
  Attach16Regular,
  Board20Regular,
  CalendarLtr20Regular,
  CheckmarkCircle20Regular,
  ChevronDown20Regular,
  Clock16Regular,
  Comment16Regular,
  Delete20Regular,
  Dismiss24Regular,
  Document20Regular,
  ErrorCircle20Regular,
  Filter20Regular,
  Flag16Filled,
  MoreHorizontal20Regular,
  PersonAdd20Regular,
  TaskListSquareLtrRegular,
  TextBulletListSquare20Regular,
  ArrowSync20Regular,
  Target20Regular,
  Mail20Regular,
  PersonCircle20Regular,
  Timer20Regular,
  Trophy20Regular,
  Rocket20Regular,
  Library20Regular,
  DataBarVertical20Regular,
  Headset20Regular,
  PlugConnected20Regular,
  Open20Regular,
  WeatherSunny20Regular,
  ClipboardTaskListLtr20Regular,
  Apps20Regular,
  Globe20Regular,
  Wrench20Regular,
  BranchFork20Regular,
  Building20Regular,
  DataArea20Regular,
  People20Regular,
  ShieldKeyhole20Regular,
  LocalLanguage20Regular,
  Phone20Regular,
  Database20Regular,
  Map20Regular,
  Warning20Regular,
  DataTrending20Regular,
} from "@fluentui/react-icons";
import CilpronCalenders from "./TaskCalender/TaskCalender";
import styles from "./TaskManager.module.css";
import px from "./Taskpages.module.css";
import {
  ApiError,
  PRIORITIES,
  R,
  api,
  connectLive,
  createTask as apiCreateTask,
  crud,
  deleteTask as apiDeleteTask,
  addComment,
  getBoard,
  getComments,
  getMe,
  getPeople,
  getProjects,
  getTaskDetail,
  hhmmOf,
  iso,
  moveTask as apiMoveTask,
  msgOf,
  parseISO,
  prioFromApi,
  prioToApi,
  setItemDone,
  TASKS_API,
} from "./Api/Taskapi";
import type { BoardCard, BoardColumn, CommentRow, Me, Person, Priority, Project, TaskDetail } from "./Api/Taskapi";
import {
  AutomationPage,
  DocsPage,
  GoalsPage,
  InboxPage,
  IntegrationsPage,
  MyTasksPage,
  ReportsPage,
  SettingsPage,
  SprintsPage,
  TicketsPage,
  TimePage,
} from "./Taskpages";
import type { Notify, PageCtx } from "./Taskpages";
import { TimelinePage, WorkloadPage, RoadmapPage, BaselinesRisksPage, FindPage, SprintPlanningPage } from "./Taskplanning";
import { MyDayPage, ListsPage, HabitsPage, FocusPage, MatrixPage, RoutinesPage } from "./Taskpersonal";
import { PortalPage, ServiceAdminPage, WorkflowsPage, AssistantPage, VerticalsPage } from "./Taskdesk";
import { DashboardsPage, PeoplePage, AccessPage, LocalizationPage, DevicesPage, ConnectorsPage } from "./Taskadmin";
import { DataPage } from "./Taskdata";
import { BulkDialog, ProjectSettingsDialog, QuickAddBar, TaskExtras, TemplatesDialog } from "./taskExtras";

/* ------------------------------------------------------------------ *
 *  TaskManager – project sidebar + Kanban / List / Calendar views,
 *  wired to the Task Manager API (https://tasks.cilpron.com).
 *   - boards, statuses, people and tasks come from the API
 *   - drag a card from one column and drop it on another (or between
 *     two cards to re-order); every card also has a "Move to" menu
 *   - Calendar view is the one central calendar (Calender.tsx), which
 *     also shows your tasks as a "Tasks" layer
 *   - the other pages (Inbox, My Tasks, Time, Goals, Sprints, Docs,
 *     Reports, Tickets, Integrations, Automation, Settings) are in
 *     TaskPages.tsx
 * ------------------------------------------------------------------ */

type ViewKey = "kanban" | "list" | "calendar";

/** a board column as the screens use it */
interface Col {
  id: number;
  label: string;
  category: string;
  tone: string;
  dot: string;
  icon: React.ReactNode;
  wip: number | null;
  cards: BoardCard[];
}

const toneOf = (c: BoardColumn): { tone: string; dot: string; icon: React.ReactNode } => {
  const cat = (c.category || "").toUpperCase();
  if (cat === "DONE") return { tone: "toneDone", dot: "#2ea043", icon: <CheckmarkCircle20Regular /> };
  if (cat === "TODO") return { tone: "toneTodo", dot: "#2f80ed", icon: <Target20Regular /> };
  if (/review|qa|test|approv/i.test(c.name)) return { tone: "toneReview", dot: "#e5484d", icon: <ErrorCircle20Regular /> };
  return { tone: "toneProcess", dot: "#f2994a", icon: <ArrowSync20Regular /> };
};

/* ---------- dates ---------- */
const pad = (n: number) => String(n).padStart(2, "0");
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDue = (s: string) => {
  const d = parseISO(s);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
};
const fmtShort = (s: string) => {
  const d = parseISO(s);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const fmtStamp = (s: string) => {
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* ---------- small pieces ---------- */
const Team: React.FC<{ names: string[] }> = ({ names }) =>
  names.length === 0 ? (
    <span className={styles.muted}>—</span>
  ) : (
    <AvatarGroup layout="stack" size={24}>
      {names.map((n, i) => (
        <AvatarGroupItem key={`${n}${i}`} name={n} color="colorful" />
      ))}
    </AvatarGroup>
  );

const PriorityPill: React.FC<{ p: Priority }> = ({ p }) => (
  <span className={`${styles.prio} ${styles["prio" + p]}`}>
    <Flag16Filled /> {p}
  </span>
);

const Segments: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <div className={styles.segs} aria-hidden="true">
    {Array.from({ length: Math.max(total, 1) }, (_, i) => (
      <span key={i} className={i < done ? styles.segOn : styles.segOff} />
    ))}
  </div>
);

const namesOf = (c: BoardCard) => (c.assignees ?? []).map((a) => a.displayName || `User ${a.userId}`);

const TaskMenu: React.FC<{
  card: BoardCard;
  cols: Col[];
  currentColId: number;
  onMove: (id: number, statusId: number) => void;
  onOpen: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ card, cols, currentColId, onMove, onOpen, onDelete }) => (
  <Menu>
    <MenuTrigger disableButtonEnhancement>
      <Button appearance="subtle" size="small" icon={<MoreHorizontal20Regular />} aria-label={`Options for ${card.title}`} />
    </MenuTrigger>
    <MenuPopover>
      <MenuList>
        <MenuItem icon={<Open20Regular />} onClick={() => onOpen(card.id)}>
          Open details
        </MenuItem>
        {cols
          .filter((c) => c.id !== currentColId)
          .map((c) => (
            <MenuItem key={c.id} icon={<ArrowMove20Regular />} onClick={() => onMove(card.id, c.id)}>
              Move to {c.label}
            </MenuItem>
          ))}
        <MenuItem icon={<Delete20Regular />} onClick={() => onDelete(card.id)}>
          Delete task
        </MenuItem>
      </MenuList>
    </MenuPopover>
  </Menu>
);

/* ---------- New Task dialog ---------- */
interface NewTaskValues {
  title: string;
  desc: string;
  due: string;
  time: string;
  priority: Priority;
  assigneeIds: number[];
}

const NewTaskForm: React.FC<{ people: Person[]; busy: boolean; onCreate: (v: NewTaskValues) => void }> = ({ people, busy, onCreate }) => {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [due, setDue] = useState<Date | null>(null);
  const [time, setTime] = useState("09:00");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [assignees, setAssignees] = useState<string[]>([]);

  const submit = () => {
    if (!title.trim() || busy) return;
    onCreate({
      title: title.trim(),
      desc: desc.trim(),
      due: iso(due || addDays(new Date(), 7)),
      time,
      priority,
      assigneeIds: assignees.map(Number),
    });
  };

  return (
    <>
      <DialogContent className={styles.formBody}>
        <Field label="Title" required>
          <Input value={title} onChange={(_, d) => setTitle(d.value)} placeholder="Home Page Redesign" />
        </Field>
        <Field label="Project description">
          <Textarea value={desc} onChange={(_, d) => setDesc(d.value)} placeholder="Improve visual hierarchy…" rows={3} resize="vertical" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Due Date" className={styles.grow}>
            <DatePicker
              value={due}
              onSelectDate={(d) => setDue(d ?? null)}
              placeholder="Select date"
              formatDate={(d) => (d ? fmtDue(iso(d)) : "")}
              allowTextInput={false}
            />
          </Field>
          <Field label="Time (calendar slot)">
            <Input type="time" value={time} onChange={(_, d) => setTime(d.value || "09:00")} />
          </Field>
        </div>
        <Field label="Select priority">
          <div className={styles.prioPick} role="radiogroup" aria-label="Priority">
            {PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={priority === p}
                className={`${styles.prio} ${styles["prio" + p]} ${styles.prioBtn} ${priority === p ? styles.prioOn : ""}`}
                onClick={() => setPriority(p)}
              >
                <Flag16Filled /> {p}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Assigned">
          <Combobox multiselect placeholder="Select team" selectedOptions={assignees} onOptionSelect={(_, d) => setAssignees(d.selectedOptions)}>
            {people.map((p) => (
              <Option key={p.id} value={String(p.id)} text={p.name}>
                <Persona avatar={{ color: "colorful", "aria-hidden": true }} name={p.name} />
              </Option>
            ))}
          </Combobox>
        </Field>
        <Field label="Attachments" hint="Files can’t be uploaded yet. Paste a link in the description instead.">
          <div className={styles.drop}>
            <Button icon={<Document20Regular />} disabled>
              Browse Files
            </Button>
            <span className={styles.muted}>Upload isn’t available yet</span>
          </div>
        </Field>
      </DialogContent>
      <DialogActions>
        <DialogTrigger disableButtonEnhancement>
          <Button appearance="secondary">Cancel</Button>
        </DialogTrigger>
        <Button className={styles.primary} appearance="primary" disabled={!title.trim() || busy} onClick={submit}>
          {busy ? "Creating…" : "Create Task"}
        </Button>
      </DialogActions>
    </>
  );
};

/* ---------- task details: status, priority, due date, checklist, comments ---------- */
const TaskDetailDialog: React.FC<{
  id: number | null;
  workspaceId: number;
  cols: Col[];
  onClose: () => void;
  onChanged: () => void;
  onMove: (taskId: number, statusId: number) => Promise<void>;
  onDelete: (taskId: number) => Promise<void>;
  onOpen: (taskId: number) => void;
  notify: Notify;
  ctx: PageCtx;
}> = ({ id, workspaceId, cols, onClose, onChanged, onMove, onDelete, onOpen, notify, ctx }) => {
  const [d, setD] = useState<TaskDetail | null>(null);
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(
    async (taskId: number) => {
      try {
        const [t, c] = await Promise.all([getTaskDetail(taskId), getComments(taskId)]);
        setD(t);
        setComments(c);
      } catch (e) {
        notify("error", msgOf(e));
        onClose();
      } finally {
        setLoading(false);
      }
    },
    [notify, onClose],
  );

  useEffect(() => {
    setD(null);
    setComments([]);
    setText("");
    if (id !== null) {
      setLoading(true);
      void load(id);
    }
  }, [id, load]);

  const patch = async (body: Record<string, unknown>) => {
    if (!d) return;
    try {
      await crud.update(R.task, d.id, body);
      await load(d.id);
      onChanged();
    } catch (e) {
      notify("error", msgOf(e));
    }
  };

  const send = async () => {
    if (!d || !text.trim() || sending) return;
    setSending(true);
    try {
      await addComment(workspaceId, d.id, text.trim());
      setText("");
      setComments(await getComments(d.id));
      onChanged();
    } catch (e) {
      notify("error", msgOf(e));
    } finally {
      setSending(false);
    }
  };

  const toggleItem = async (itemId: number, isDone: boolean) => {
    setD((cur) =>
      cur
        ? { ...cur, checklists: (cur.checklists ?? []).map((cl) => ({ ...cl, items: (cl.items ?? []).map((it) => (it.id === itemId ? { ...it, isDone } : it)) })) }
        : cur,
    );
    try {
      await setItemDone(itemId, isDone);
      onChanged();
    } catch (e) {
      notify("error", msgOf(e));
      if (d) await load(d.id);
    }
  };

  const colId = d?.statusId ?? null;

  return (
    <Dialog open={id !== null} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={`${styles.dialog} ${px.detailSurface}`}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {d ? d.title : "Task"}
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            {loading && !d && <Spinner size="small" label="Loading…" />}
            {d && (
              <>
                <div className={styles.formRow}>
                  <Field label="Status" className={styles.grow}>
                    <Select
                      value={colId === null ? "" : String(colId)}
                      onChange={(_, o) => {
                        const sid = Number(o.value);
                        if (sid) void onMove(d.id, sid).then(() => load(d.id));
                      }}
                    >
                      {cols.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Priority" className={styles.grow}>
                    <Select value={prioFromApi(d.priority)} onChange={(_, o) => void patch({ priority: prioToApi(o.value as Priority) })}>
                      {PRIORITIES.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Due date" className={styles.grow}>
                    <Input
                      type="date"
                      value={d.dueDate ? d.dueDate.slice(0, 10) : ""}
                      onChange={(_, o) => o.value && void patch({ dueDate: o.value })}
                    />
                  </Field>
                </div>

                <div className={px.metaLine}>
                  <span className={styles.muted}>Assigned for</span>
                  <Team names={(d.assignees ?? []).map((a) => a.displayName || `User ${a.userId}`)} />
                  {d.dueAt && (
                    <span className={px.metaChip}>
                      <Clock16Regular /> {hhmmOf(d.dueAt)}
                    </span>
                  )}
                </div>

                {d.description && <p className={px.descText}>{d.description}</p>}

                {(d.checklists ?? []).map((cl) => (
                  <div key={cl.id} className={px.block}>
                    <div className={px.blockTitle}>{cl.title}</div>
                    {(cl.items ?? []).map((it) => (
                      <Checkbox key={it.id} checked={it.isDone} label={it.text} onChange={(_, o) => void toggleItem(it.id, o.checked === true)} />
                    ))}
                    {(cl.items ?? []).length === 0 && <span className={styles.muted}>No items</span>}
                  </div>
                ))}

                {(d.subtasks ?? []).length > 0 && (
                  <div className={px.block}>
                    <div className={px.blockTitle}>Subtasks</div>
                    {(d.subtasks ?? []).map((s) => (
                      <div key={s.id} className={px.subRow}>
                        <Checkbox checked={s.isCompleted} disabled label={s.title} />
                        {s.dueDate && <span className={styles.muted}>{fmtShort(s.dueDate)}</span>}
                      </div>
                    ))}
                  </div>
                )}

                <div className={px.block}>
                  <div className={px.blockTitle}>Comments ({comments.length})</div>
                  {comments.map((c) => (
                    <div key={c.id} className={px.comment}>
                      <div className={px.commentMeta}>
                        {fmtStamp(c.createdAt)}
                        {c.isEdited ? " · edited" : ""}
                      </div>
                      <div>{c.body}</div>
                    </div>
                  ))}
                  <div className={px.commentBox}>
                    <Textarea value={text} onChange={(_, o) => setText(o.value)} placeholder="Write a comment…" rows={2} resize="vertical" className={styles.grow} />
                    <Button className={styles.primary} appearance="primary" disabled={!text.trim() || sending} onClick={() => void send()}>
                      Send
                    </Button>
                  </div>
                </div>

                <TaskExtras
                  key={d.id}
                  taskId={d.id}
                  workspaceId={d.workspaceId}
                  projectId={d.projectId ?? null}
                  isCompleted={d.isCompleted}
                  ctx={ctx}
                  onChanged={() => {
                    void load(d.id);
                    onChanged();
                  }}
                  onOpen={onOpen}
                  onClosed={onClose}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            {d && (
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                onClick={() => {
                  void onDelete(d.id).then(onClose);
                }}
              >
                Delete task
              </Button>
            )}
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- invite + new project ---------- */
const InviteDialog: React.FC<{ open: boolean; workspaceId: number; onClose: () => void; notify: Notify; onDone: () => void }> = ({ open, workspaceId, onClose, notify, onDone }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("MEMBER");
    }
  }, [open]);
  const send = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await api("POST", `/api/workspaces/${workspaceId}/members/invite`, { email: email.trim(), role });
      notify("success", `${email.trim()} was added to the workspace.`);
      onDone();
      onClose();
    } catch (e) {
      notify("error", e instanceof ApiError && e.status === 404 ? "That person has not signed in to the Task Manager yet." : msgOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Invite to workspace
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Field label="Email" required hint="They need to have signed in once.">
              <Input type="email" value={email} onChange={(_, o) => setEmail(o.value)} placeholder="name@company.com" />
            </Field>
            <Field label="Role">
              <Select value={role} onChange={(_, o) => setRole(o.value)}>
                <option value="ADMIN">Admin</option>
                <option value="MEMBER">Member</option>
                <option value="GUEST">Guest</option>
                <option value="VIEWER">Viewer</option>
              </Select>
            </Field>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button className={styles.primary} appearance="primary" disabled={!email.trim() || busy} onClick={() => void send()}>
              {busy ? "Inviting…" : "Invite"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const SWATCHES = ["#2ea043", "#0f6cbd", "#f2994a", "#e5484d", "#8764b8", "#038387"];
/** the four columns every new project starts with */
const DEFAULT_STATUSES: { name: string; category: string; color: string }[] = [
  { name: "To Do", category: "TODO", color: "#2f80ed" },
  { name: "On Process", category: "IN_PROGRESS", color: "#f2994a" },
  { name: "On Review", category: "IN_PROGRESS", color: "#e5484d" },
  { name: "Completed", category: "DONE", color: "#2ea043" },
];

const NewProjectDialog: React.FC<{ open: boolean; workspaceId: number; onClose: () => void; notify: Notify; onCreated: (id: number) => void }> = ({ open, workspaceId, onClose, notify, onCreated }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState(SWATCHES[0] as string);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setName("");
      setDesc("");
      setColor(SWATCHES[0] as string);
    }
  }, [open]);
  const create = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const p = await crud.create<{ id: number }>(R.project, {
        workspaceId,
        name: name.trim(),
        description: desc.trim() || null,
        color,
        projectKey: name.trim().replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase() || null,
        projectType: "BOARD",
        defaultView: "BOARD",
      });
      for (let i = 0; i < DEFAULT_STATUSES.length; i++) {
        const s = DEFAULT_STATUSES[i] as (typeof DEFAULT_STATUSES)[number];
        await crud.create(R.status, { workspaceId, projectId: p.id, name: s.name, category: s.category, color: s.color, sortOrder: (i + 1) * 10, isDefault: i === 0 });
      }
      notify("success", `Project “${name.trim()}” created.`);
      onCreated(p.id);
      onClose();
    } catch (e) {
      notify("error", msgOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            New Project
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Field label="Name" required>
              <Input value={name} onChange={(_, o) => setName(o.value)} placeholder="Product Launch 2026" />
            </Field>
            <Field label="Description">
              <Textarea value={desc} onChange={(_, o) => setDesc(o.value)} rows={2} resize="vertical" />
            </Field>
            <Field label="Colour">
              <div className={px.swatches} role="radiogroup" aria-label="Colour">
                {SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={color === c}
                    aria-label={c}
                    className={`${px.swatch} ${color === c ? px.swatchOn : ""}`}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </Field>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button className={styles.primary} appearance="primary" disabled={!name.trim() || busy} onClick={() => void create()}>
              {busy ? "Creating…" : "Create Project"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- the board ---------- */
interface DropTarget {
  col: number;
  /** id of the card the dragged card lands in front of; null = end of the column */
  beforeId: number | null;
}

type Nav =
  | "project" | "inbox" | "my" | "automation" | "time" | "goals" | "sprints" | "docs" | "reports" | "tickets" | "integrations" | "settings" | "help"
  | "myday" | "lists" | "habits" | "focus" | "matrix" | "routines"
  | "timeline" | "workload" | "roadmap" | "baselines" | "find" | "sprintplan"
  | "portal" | "svcadmin" | "workflows" | "assistant" | "verticals"
  | "dashboards" | "people" | "access" | "locale" | "devices" | "connectors" | "data";

const HelpPage: React.FC = () => (
  <>
    <div className={styles.head}>
      <div className={styles.headLeft}>
        <span className={styles.logo}>
          <QuestionCircle20Regular />
        </span>
        <div>
          <h2 className={styles.title}>Help Center</h2>
          <p className={styles.sub}>Where things are and how they connect.</p>
        </div>
      </div>
    </div>
    <div className={styles.body}>
      <div className={px.pageBody}>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Boards</h3>
          <p className={px.text}>Drag a card to another column to change its status, or between two cards to re-order. Click a task to open comments and the checklist.</p>
        </div>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Calendar</h3>
          <p className={px.text}>The Calendar view is your central calendar. Tasks with a due date show up in it as the “Tasks” layer. To see them in Outlook or Google Calendar, use the private link under Integrations.</p>
        </div>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Direct messages</h3>
          <p className={px.text}>Direct messages live in the central messaging app, not in the Task Manager.</p>
        </div>
        <div className={px.card}>
          <h3 className={px.cardTitle}>API reference</h3>
          <p className={px.text}>
            <a href={`${TASKS_API}/docs`} target="_blank" rel="noreferrer">
              {TASKS_API}/docs
            </a>
          </p>
        </div>
      </div>
    </div>
  </>
);

const TaskManager: React.FC = () => {
  const [me, setMe] = useState<Me | null>(null);
  const [boot, setBoot] = useState<"loading" | "ready" | "error">("loading");
  const [bootError, setBootError] = useState("");
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [board, setBoard] = useState<BoardColumn[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [nav, setNav] = useState<Nav>("project");
  const [view, setView] = useState<ViewKey>("kanban");
  const [query, setQuery] = useState("");
  const [prio, setPrio] = useState<string[]>([]);
  const [dialog, setDialog] = useState<{ statusId: number } | null>(null);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [shut, setShut] = useState<Record<string, boolean>>({ personal: true, plan: true, service: true, automate: true, insights: true, admin: true });
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [drop, setDrop] = useState<DropTarget | null>(null);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [unread, setUnread] = useState(0);
  const [notice, setNotice] = useState<{ intent: "success" | "error" | "info"; text: string } | null>(null);
  const dragRef = useRef<number | null>(null);
  const boardReq = useRef(0);
  const noticeTimer = useRef(0);

  const notify: Notify = useCallback((intent, text) => {
    setNotice({ intent, text });
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 6000);
  }, []);

  /* ---------- sign-in + workspace ---------- */
  useEffect(() => {
    let live = true;
    getMe()
      .then((m) => {
        if (!live) return;
        setMe(m);
        const ws = m.defaultWorkspaceId ?? m.workspaces[0]?.id ?? null;
        setWorkspaceId(ws);
        if (ws === null) {
          setBootError("You don’t belong to a workspace yet. Ask an admin to invite you.");
          setBoot("error");
        } else setBoot("ready");
      })
      .catch((e: unknown) => {
        if (!live) return;
        setBootError(msgOf(e));
        setBoot("error");
      });
    return () => {
      live = false;
    };
  }, []);

  const loadProjects = useCallback(
    async (ws: number, select?: number) => {
      try {
        const list = await getProjects(ws);
        setProjects(list);
        setProjectId((cur) => {
          if (select !== undefined) return select;
          if (cur !== null && list.some((p) => p.id === cur)) return cur;
          return list[0]?.id ?? null;
        });
      } catch (e) {
        notify("error", msgOf(e));
      }
    },
    [notify],
  );

  const loadPeople = useCallback(
    async (ws: number) => {
      try {
        setPeople(await getPeople(ws));
      } catch {
        setPeople([]);
      }
    },
    [],
  );

  const refreshUnread = useCallback(async () => {
    try {
      const r = await api<{ unread: number }>("GET", "/api/notifications/unread-count");
      setUnread(r?.unread ?? 0);
    } catch {
      /* the badge is optional */
    }
  }, []);

  useEffect(() => {
    if (workspaceId === null) return;
    setProjects([]);
    setProjectId(null);
    setBoard([]);
    void loadProjects(workspaceId);
    void loadPeople(workspaceId);
    void refreshUnread();
  }, [workspaceId, loadProjects, loadPeople, refreshUnread]);

  const loadBoard = useCallback(
    async (pid: number, silent = false) => {
      const req = ++boardReq.current;
      if (!silent) setBoardLoading(true);
      try {
        const cols = await getBoard(pid);
        if (req === boardReq.current && dragRef.current === null) setBoard(cols);
      } catch (e) {
        if (req === boardReq.current) notify("error", msgOf(e));
      } finally {
        if (req === boardReq.current) setBoardLoading(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    if (projectId === null) {
      setBoard([]);
      return;
    }
    void loadBoard(projectId);
  }, [projectId, loadBoard]);

  /* live: another person changes this project, or a notification arrives */
  useEffect(() => {
    if (boot !== "ready") return;
    return connectLive(projectId, {
      onProject: () => {
        if (projectId !== null) void loadBoard(projectId, true);
      },
      onNotification: () => void refreshUnread(),
    });
  }, [boot, projectId, loadBoard, refreshUnread]);

  const project = projects.find((p) => p.id === projectId) ?? null;

  /* ---------- columns for the screens ---------- */
  const cols: Col[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...board]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((c) => {
        const t = toneOf(c);
        const cards = (c.tasks ?? [])
          .filter((k) => !k.parentTaskId)
          .filter(
            (k) =>
              (!q || k.title.toLowerCase().includes(q) || (k.description ?? "").toLowerCase().includes(q)) &&
              (prio.length === 0 || prio.includes(prioFromApi(k.priority))),
          );
        return { id: c.id, label: c.name, category: c.category, tone: t.tone, dot: c.color || t.dot, icon: t.icon, wip: c.wipLimit ?? null, cards };
      });
  }, [board, query, prio]);

  const colOfCard = (cardId: number) => cols.find((c) => c.cards.some((k) => k.id === cardId));

  /* ---------- actions ---------- */
  const reload = useCallback(() => {
    if (projectId !== null) void loadBoard(projectId, true);
  }, [projectId, loadBoard]);

  /* move a task to a column; beforeId = card to land in front of (null = end of column) */
  const moveCard = async (id: number, statusId: number, beforeId: number | null = null) => {
    const before = board;
    // show it where it was dropped straight away
    setBoard((cur) => {
      let moving: BoardCard | undefined;
      const without = cur.map((c) => {
        const found = (c.tasks ?? []).find((k) => k.id === id);
        if (found) moving = found;
        return { ...c, tasks: (c.tasks ?? []).filter((k) => k.id !== id) };
      });
      if (!moving) return cur;
      const m: BoardCard = moving;
      return without.map((c) => {
        if (c.id !== statusId) return c;
        const list = c.tasks ?? [];
        const at = beforeId === null ? -1 : list.findIndex((k) => k.id === beforeId);
        const card = { ...m, isCompleted: (c.category || "").toUpperCase() === "DONE" };
        return { ...c, tasks: at < 0 ? [...list, card] : [...list.slice(0, at), card, ...list.slice(at)] };
      });
    });
    try {
      await apiMoveTask(id, statusId, beforeId);
      reload();
    } catch (e) {
      setBoard(before);
      notify("error", e instanceof ApiError && e.status === 409 ? "This task is blocked by another task that isn’t finished yet." : msgOf(e));
      reload();
    }
  };

  const removeCard = async (id: number) => {
    try {
      await apiDeleteTask(id);
      notify("success", "Task deleted.");
      reload();
    } catch (e) {
      notify("error", msgOf(e));
    }
  };

  const createTask = async (statusId: number, v: NewTaskValues) => {
    if (workspaceId === null || projectId === null) return;
    setCreating(true);
    try {
      await apiCreateTask({
        workspaceId,
        projectId,
        statusId,
        title: v.title,
        description: v.desc,
        priority: v.priority,
        dueDate: v.due,
        time: v.time,
        assigneeIds: v.assigneeIds,
      });
      setDialog(null);
      notify("success", "Task created.");
      reload();
    } catch (e) {
      notify("error", msgOf(e));
    } finally {
      setCreating(false);
    }
  };

  /* ---------- drag & drop ---------- */
  const endDrag = () => {
    dragRef.current = null;
    setDragId(null);
    setDrop(null);
  };

  const onCardDragOver = (e: React.DragEvent<HTMLDivElement>, col: number, id: number, list: BoardCard[]) => {
    if (dragId === null) return;
    e.preventDefault();
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    const idx = list.findIndex((t) => t.id === id);
    const next = after ? (list[idx + 1]?.id ?? null) : id;
    setDrop((d) => (d && d.col === col && d.beforeId === next ? d : { col, beforeId: next }));
  };

  const onColumnDragOver = (e: React.DragEvent<HTMLDivElement>, col: number) => {
    if (dragId === null) return;
    e.preventDefault();
    setDrop((d) => (d && d.col === col && d.beforeId === null ? d : { col, beforeId: null }));
  };

  const onColumnDrop = (e: React.DragEvent<HTMLDivElement>, col: number) => {
    e.preventDefault();
    const id = dragId ?? Number(e.dataTransfer.getData("text/plain"));
    const beforeId = drop && drop.col === col ? drop.beforeId : null;
    endDrag();
    if (id) void moveCard(id, col, beforeId !== id ? beforeId : null);
  };

  const onColumnDragLeave = (e: React.DragEvent<HTMLDivElement>, col: number) => {
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    setDrop((d) => (d && d.col === col ? null : d));
  };

  /* ---------- shared with the other pages ---------- */
  const ctx: PageCtx | null =
    me && workspaceId !== null
      ? {
          me,
          workspaceId,
          projects,
          projectId,
          people,
          notify,
          openTask: (id: number) => setDetailId(id),
          reloadProjects: () => void loadProjects(workspaceId),
          refreshUnread: () => void refreshUnread(),
        }
      : null;

  const firstColId = cols[0]?.id ?? 0;

  const sideBtn = (key: Nav, label: string, icon: React.ReactNode, badge?: number) => (
    <button type="button" className={`${styles.sideItem} ${nav === key ? styles.sideActive : ""}`} onClick={() => setNav(key)}>
      {icon}
      <span className={px.sideLabel}>{label}</span>
      {badge ? <span className={px.badge}>{badge > 99 ? "99+" : badge}</span> : null}
    </button>
  );

  const group = (key: string, label: string, items: [Nav, string, React.ReactNode, number?][]) => {
    const closed = !!shut[key] && !items.some(([k]) => k === nav);
    return (
      <div className={styles.sideGroup} key={key}>
        <button type="button" className={`${styles.sideHead} ${px.groupHead}`} aria-expanded={!closed} onClick={() => setShut((o) => ({ ...o, [key]: !o[key] }))}>
          <span>{label}</span>
          <ChevronDown20Regular className={closed ? (px.chevShut ?? "") : ""} />
        </button>
        {!closed && items.map(([k, l, i, b]) => <React.Fragment key={k}>{sideBtn(k, l, i, b)}</React.Fragment>)}
      </div>
    );
  };

  /* ---------- states before the app is ready ---------- */
  if (boot !== "ready" || !ctx || workspaceId === null || !me) {
    return (
      <div className={styles.oxRoot}>
        <FluentProvider theme={webLightTheme} className={styles.shell}>
          <main className={styles.main}>
            <div className={px.centerState}>
              {boot === "loading" ? (
                <Spinner label="Opening the Task Manager…" />
              ) : (
                <>
                  <h2 className={styles.title}>Can’t open the Task Manager</h2>
                  <p className={styles.sub}>{bootError}</p>
                  <Button appearance="primary" className={styles.primary} onClick={() => window.location.reload()}>
                    Try again
                  </Button>
                </>
              )}
            </div>
          </main>
        </FluentProvider>
      </div>
    );
  }

  const workspaceName = me.workspaces.find((w) => w.id === workspaceId)?.name ?? "Workspace";
  const projectBody = (
    <>
      {/* ---------- header ---------- */}
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <span className={styles.logo}>
            <TaskListSquareLtrRegular fontSize={22} />
          </span>
          <div>
            <h2 className={styles.title}>{project ? project.name : "No project yet"}</h2>
            <p className={styles.sub}>{project ? project.description || "Monitor all of your task here." : "Create a project to start adding tasks."}</p>
          </div>
        </div>
        <div className={styles.headRight}>
          <AvatarGroup layout="stack" size={32}>
            {people.slice(0, 5).map((p) => (
              <AvatarGroupItem key={p.id} name={p.name} color="colorful" />
            ))}
          </AvatarGroup>
          <Button icon={<PersonAdd20Regular />} onClick={() => setInviteOpen(true)}>
            Invite
          </Button>
        </div>
      </div>

      {/* ---------- toolbar ---------- */}
      <div className={styles.bar}>
        <TabList selectedValue={view} onTabSelect={(_: SelectTabEvent, d: SelectTabData) => setView(d.value as ViewKey)}>
          <Tab value="kanban" icon={<Board20Regular />}>
            Kanban View
          </Tab>
          <Tab value="list" icon={<TextBulletListSquare20Regular />}>
            List View
          </Tab>
          <Tab value="calendar" icon={<CalendarLtr20Regular />}>
            Calendar View
          </Tab>
        </TabList>
        {view !== "calendar" && (
          <div className={styles.tools}>
            <SearchBox className={styles.search} placeholder="Search" value={query} onChange={(_, d) => setQuery(d.value)} />
            <Menu checkedValues={{ priority: prio }} onCheckedValueChange={(_, d) => setPrio(d.checkedItems)}>
              <MenuTrigger disableButtonEnhancement>
                <Button icon={<Filter20Regular />}>Filter{prio.length ? ` (${prio.length})` : ""}</Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {PRIORITIES.map((p) => (
                    <MenuItemCheckbox key={p} name="priority" value={p}>
                      {p} priority
                    </MenuItemCheckbox>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} disabled={cols.length === 0} onClick={() => setDialog({ statusId: firstColId })}>
              Create Task
            </Button>
          </div>
        )}
      </div>

      {project && view !== "calendar" && ctx && (
        <div className={px.extraBar}>
          <QuickAddBar ctx={ctx} onAdded={reload} />
          <div className={px.extraBtns}>
            {selected.length > 0 && (
              <>
                <Button appearance="primary" className={styles.primary} onClick={() => setBulkOpen(true)}>
                  Edit {selected.length} selected
                </Button>
                <Button appearance="subtle" onClick={() => setSelected([])}>
                  Clear
                </Button>
              </>
            )}
            <Button onClick={() => setTemplatesOpen(true)}>Templates</Button>
            <Button onClick={() => setSettingsOpen(true)}>Project settings</Button>
          </div>
        </div>
      )}

      {/* ---------- views ---------- */}
      <div className={styles.body}>
        {view === "calendar" && (
          <div className={px.calHost}>
            <CilpronCalenders />
          </div>
        )}

        {view !== "calendar" && !project && !boardLoading && (
          <div className={px.centerState}>
            <h3 className={px.cardTitle}>No projects in {workspaceName} yet</h3>
            <Button appearance="primary" className={styles.primary} icon={<Add20Regular />} onClick={() => setProjectOpen(true)}>
              New Project
            </Button>
          </div>
        )}

        {view !== "calendar" && project && boardLoading && board.length === 0 && (
          <div className={px.centerState}>
            <Spinner label="Loading board…" />
          </div>
        )}

        {view !== "calendar" && project && !boardLoading && board.length === 0 && (
          <div className={px.centerState}>
            <h3 className={px.cardTitle}>This project has no columns yet</h3>
            <p className={styles.sub}>Columns come from the project’s statuses (To Do, On Process…).</p>
            <Button
              appearance="primary"
              className={styles.primary}
              onClick={async () => {
                try {
                  for (let i = 0; i < DEFAULT_STATUSES.length; i++) {
                    const s = DEFAULT_STATUSES[i] as (typeof DEFAULT_STATUSES)[number];
                    await crud.create(R.status, { workspaceId, projectId: project.id, name: s.name, category: s.category, color: s.color, sortOrder: (i + 1) * 10, isDefault: i === 0 });
                  }
                  reload();
                } catch (e) {
                  notify("error", msgOf(e));
                }
              }}
            >
              Add the standard columns
            </Button>
          </div>
        )}

        {view === "kanban" && project && board.length > 0 && (
          <div className={styles.board}>
            {cols.map((c) => {
              const list = c.cards;
              const over = drop?.col === c.id;
              return (
                <section key={c.id} className={styles.col} aria-label={c.label}>
                  <header className={`${styles.colHead} ${styles[c.tone]}`}>
                    <span className={styles.colName}>
                      {c.icon}
                      {c.label}
                      <span className={styles.colCount}>{list.length}</span>
                    </span>
                    <Button appearance="subtle" size="small" icon={<Add20Regular />} aria-label={`Add task to ${c.label}`} onClick={() => setDialog({ statusId: c.id })} />
                  </header>
                  <div
                    className={`${styles.colBody} ${over ? styles.colOver : ""}`}
                    onDragOver={(e) => onColumnDragOver(e, c.id)}
                    onDragLeave={(e) => onColumnDragLeave(e, c.id)}
                    onDrop={(e) => onColumnDrop(e, c.id)}
                  >
                    {list.map((t) => (
                      <React.Fragment key={t.id}>
                        {over && drop?.beforeId === t.id && t.id !== dragId && <div className={styles.dropLine} />}
                        <div
                          className={`${styles.card} ${dragId === t.id ? styles.dragging : ""}`}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", String(t.id));
                            dragRef.current = t.id;
                            setDragId(t.id);
                          }}
                          onDragEnd={endDrag}
                          onDragOver={(e) => onCardDragOver(e, c.id, t.id, list)}
                        >
                          <div className={styles.cardTop}>
                            <span className={styles.dueChip}>
                              <Clock16Regular /> {t.dueDate ? `Due: ${fmtDue(t.dueDate)}` : "No due date"}
                            </span>
                            <TaskMenu card={t} cols={cols} currentColId={c.id} onMove={(id, s) => void moveCard(id, s)} onOpen={setDetailId} onDelete={(id) => void removeCard(id)} />
                          </div>
                          <h4 className={styles.cardTitle} style={{ cursor: "pointer" }} onClick={() => setDetailId(t.id)}>
                            {t.title}
                          </h4>
                          <p className={styles.cardDesc}>{t.description || "No description"}</p>
                          <div className={styles.mile}>
                            <span className={styles.mileLabel}>
                              <TaskListSquareLtrRegular /> {t.milestonesDone > 0 ? "Milestone" : "Progress:"}
                            </span>
                            <span>
                              {t.milestonesDone}/{t.milestonesTotal}
                            </span>
                          </div>
                          {t.milestonesDone > 0 && <Segments done={t.milestonesDone} total={t.milestonesTotal} />}
                          <div className={styles.assign}>
                            <span className={styles.muted}>Assigned for</span>
                            <Team names={namesOf(t)} />
                          </div>
                          <div className={styles.cardFoot}>
                            <PriorityPill p={prioFromApi(t.priority)} />
                            <span className={styles.counts}>
                              <span className={styles.count}>
                                <Attach16Regular /> {t.attachmentCount}
                              </span>
                              <span className={styles.count}>
                                <Comment16Regular /> {t.commentCount}
                              </span>
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                    {over && drop?.beforeId === null && <div className={styles.dropLine} />}
                    {list.length === 0 && !over && <div className={styles.empty}>Drop tasks here</div>}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {view === "list" && project && board.length > 0 && (
          <div className={styles.list}>
            {cols.map((c) => {
              const list = c.cards;
              const closed = !!collapsed[c.id];
              return (
                <section key={c.id} className={styles.group}>
                  <header className={`${styles.groupHead} ${styles[c.tone]}`}>
                    <button type="button" className={styles.groupToggle} aria-expanded={!closed} onClick={() => setCollapsed((s) => ({ ...s, [c.id]: !s[c.id] }))}>
                      <ChevronDown20Regular className={closed ? (styles.chevClosed ?? "") : ""} />
                      <span className={styles.colName}>
                        {c.icon}
                        {c.label}
                        <span className={styles.colCount}>{list.length}</span>
                      </span>
                    </button>
                    <Button appearance="subtle" size="small" icon={<Add20Regular />} aria-label={`Add task to ${c.label}`} onClick={() => setDialog({ statusId: c.id })} />
                  </header>
                  {!closed && (
                    <div className={styles.table} role="table">
                      <div className={`${styles.tr} ${styles.th}`} role="row">
                        <span>Task name</span>
                        <span>Description</span>
                        <span>Assignee</span>
                        <span>Start</span>
                        <span>Due Date</span>
                        <span>Priority</span>
                        <span>Attach</span>
                        <span>Chat</span>
                        <span />
                      </div>
                      {list.map((t) => (
                        <div key={t.id} className={styles.tr} role="row">
                          <span className={styles.cellTitle} style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${t.title}`}
                              checked={selected.includes(t.id)}
                              onChange={(e) => setSelected((cur) => (e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id)))}
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginRight: 8 }}
                            />
                            <span onClick={() => setDetailId(t.id)}>{t.title}</span>
                          </span>
                          <span className={`${styles.muted} ${styles.ellipsis}`}>{t.description}</span>
                          <span>
                            <Team names={namesOf(t)} />
                          </span>
                          <span>{t.startDate ? fmtShort(t.startDate) : "—"}</span>
                          <span>{t.dueDate ? fmtShort(t.dueDate) : "—"}</span>
                          <span>
                            <PriorityPill p={prioFromApi(t.priority)} />
                          </span>
                          <span className={styles.count}>
                            <Attach16Regular /> {t.attachmentCount}
                          </span>
                          <span className={styles.count}>
                            <Comment16Regular /> {t.commentCount}
                          </span>
                          <span>
                            <TaskMenu card={t} cols={cols} currentColId={c.id} onMove={(id, s) => void moveCard(id, s)} onOpen={setDetailId} onDelete={(id) => void removeCard(id)} />
                          </span>
                        </div>
                      ))}
                      {list.length === 0 && <div className={styles.emptyRow}>No tasks</div>}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={styles.oxRoot}>
      <FluentProvider theme={webLightTheme} className={styles.shell}>
        {/* ---------- sidebar ---------- */}
        <aside className={styles.side}>
          {me.workspaces.length > 1 && (
            <div className={styles.sideGroup}>
              <Select
                aria-label="Workspace"
                value={String(workspaceId)}
                onChange={(_, o) => {
                  setWorkspaceId(Number(o.value));
                  setNav("project");
                }}
              >
                {me.workspaces.map((w) => (
                  <option key={w.id} value={String(w.id)}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {group("general", "General", [
            ["inbox", "Inbox", <Mail20Regular />, unread],
            ["my", "My Tasks", <PersonCircle20Regular />],
          ])}
          {group("personal", "Personal", [
            ["myday", "My Day", <WeatherSunny20Regular />],
            ["lists", "Lists", <ClipboardTaskListLtr20Regular />],
            ["habits", "Habits", <Target20Regular />],
            ["focus", "Focus", <Timer20Regular />],
            ["matrix", "Priorities", <Apps20Regular />],
            ["routines", "Routines", <ArrowSync20Regular />],
          ])}

          <div className={styles.sideGroup}>
            <div className={styles.sideHead}>
              <span>Projects</span>
              <Button appearance="subtle" size="small" icon={<Add20Regular />} aria-label="New project" onClick={() => setProjectOpen(true)} />
            </div>
            {projects.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`${styles.sideItem} ${nav === "project" && projectId === p.id ? styles.sideActive : ""}`}
                onClick={() => {
                  setProjectId(p.id);
                  setNav("project");
                  setQuery("");
                }}
              >
                <Sparkle20Filled style={{ color: p.color || "#0f6cbd" }} />
                <span className={px.sideLabel}>{p.name}</span>
              </button>
            ))}
            {projects.length === 0 && <span className={`${styles.sideItem} ${styles.sideMuted}`}>No projects yet</span>}
          </div>

          {group("plan", "Plan", [
            ["timeline", "Timeline", <Board20Regular />],
            ["workload", "Workload", <DataArea20Regular />],
            ["roadmap", "Roadmap", <Map20Regular />],
            ["baselines", "Baselines & risks", <Warning20Regular />],
            ["sprints", "Sprints", <Rocket20Regular />],
            ["sprintplan", "Sprint planning", <Rocket20Regular />],
            ["find", "Find & saved views", <Filter20Regular />],
          ])}
          {group("workspace", "Workspace", [
            ["time", "Time", <Timer20Regular />],
            ["goals", "Goals", <Trophy20Regular />],
            ["docs", "Docs", <Library20Regular />],
            ["reports", "Reports", <DataBarVertical20Regular />],
            ["verticals", "Industry packs", <Building20Regular />],
          ])}
          {group("service", "Service", [
            ["tickets", "Tickets", <Headset20Regular />],
            ["portal", "Service portal", <Globe20Regular />],
            ["svcadmin", "Service desk admin", <Wrench20Regular />],
          ])}
          {group("automate", "Automate", [
            ["automation", "Automation", <Flash20Regular />],
            ["workflows", "Workflows & approvals", <BranchFork20Regular />],
          ])}
          {group("insights", "Insights", [
            ["dashboards", "Dashboards", <DataTrending20Regular />],
            ["assistant", "Assistant", <Sparkle20Filled />],
          ])}
          {group("admin", "Admin", [
            ["people", "People & teams", <People20Regular />],
            ["access", "Access & audit", <ShieldKeyhole20Regular />],
            ["locale", "Localization", <LocalLanguage20Regular />],
            ["devices", "Devices & sync", <Phone20Regular />],
            ["connectors", "Connectors", <PlugConnected20Regular />],
            ["integrations", "Integrations", <PlugConnected20Regular />],
            ["data", "All tables", <Database20Regular />],
          ])}
          <div className={`${styles.sideGroup} ${styles.sideBottom}`}>
            <div className={styles.sideHead}>
              <span>System</span>
            </div>
            {sideBtn("settings", "Settings", <Settings20Regular />)}
            {sideBtn("help", "Help Center", <QuestionCircle20Regular />)}
          </div>
        </aside>

        <main className={styles.main}>
          {notice && (
            <MessageBar intent={notice.intent} className={px.notice}>
              <MessageBarBody>{notice.text}</MessageBarBody>
            </MessageBar>
          )}
          {nav === "project" && projectBody}
          {nav === "inbox" && <InboxPage ctx={ctx} />}
          {nav === "my" && <MyTasksPage ctx={ctx} />}
          {nav === "automation" && <AutomationPage ctx={ctx} />}
          {nav === "time" && <TimePage ctx={ctx} />}
          {nav === "goals" && <GoalsPage ctx={ctx} />}
          {nav === "sprints" && <SprintsPage ctx={ctx} />}
          {nav === "docs" && <DocsPage ctx={ctx} />}
          {nav === "reports" && <ReportsPage ctx={ctx} />}
          {nav === "tickets" && <TicketsPage ctx={ctx} />}
          {nav === "integrations" && <IntegrationsPage ctx={ctx} />}
          {nav === "settings" && <SettingsPage ctx={ctx} />}
          {nav === "myday" && <MyDayPage ctx={ctx} />}
          {nav === "lists" && <ListsPage ctx={ctx} />}
          {nav === "habits" && <HabitsPage ctx={ctx} />}
          {nav === "focus" && <FocusPage ctx={ctx} />}
          {nav === "matrix" && <MatrixPage ctx={ctx} />}
          {nav === "routines" && <RoutinesPage ctx={ctx} />}
          {nav === "timeline" && <TimelinePage ctx={ctx} />}
          {nav === "workload" && <WorkloadPage ctx={ctx} />}
          {nav === "roadmap" && <RoadmapPage ctx={ctx} />}
          {nav === "baselines" && <BaselinesRisksPage ctx={ctx} />}
          {nav === "find" && <FindPage ctx={ctx} />}
          {nav === "sprintplan" && <SprintPlanningPage ctx={ctx} />}
          {nav === "portal" && <PortalPage ctx={ctx} />}
          {nav === "svcadmin" && <ServiceAdminPage ctx={ctx} />}
          {nav === "workflows" && <WorkflowsPage ctx={ctx} />}
          {nav === "assistant" && <AssistantPage ctx={ctx} />}
          {nav === "verticals" && <VerticalsPage ctx={ctx} />}
          {nav === "dashboards" && <DashboardsPage ctx={ctx} />}
          {nav === "people" && <PeoplePage ctx={ctx} />}
          {nav === "access" && <AccessPage ctx={ctx} />}
          {nav === "locale" && <LocalizationPage ctx={ctx} />}
          {nav === "devices" && <DevicesPage ctx={ctx} />}
          {nav === "connectors" && <ConnectorsPage ctx={ctx} />}
          {nav === "data" && <DataPage ctx={ctx} />}
          {nav === "help" && <HelpPage />}
        </main>

        {/* ---------- New Task dialog ---------- */}
        <Dialog open={dialog !== null} onOpenChange={(_, d) => !d.open && setDialog(null)}>
          <DialogSurface className={styles.dialog}>
            <DialogBody>
              <DialogTitle
                action={
                  <DialogTrigger action="close">
                    <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                  </DialogTrigger>
                }
              >
                New Task
              </DialogTitle>
              {dialog && <NewTaskForm key={dialog.statusId} people={people} busy={creating} onCreate={(v) => void createTask(dialog.statusId, v)} />}
            </DialogBody>
          </DialogSurface>
        </Dialog>

        <TaskDetailDialog
          id={detailId}
          workspaceId={workspaceId}
          cols={cols.length ? cols : []}
          onClose={() => setDetailId(null)}
          onChanged={reload}
          onMove={async (tid, sid) => {
            if (colOfCard(tid)?.id !== sid) await moveCard(tid, sid);
          }}
          onDelete={removeCard}
          onOpen={setDetailId}
          notify={notify}
          ctx={ctx}
        />
        <BulkDialog
          ids={selected}
          open={bulkOpen}
          columns={cols.map((c) => ({ id: c.id, label: c.label }))}
          ctx={ctx}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            setSelected([]);
            reload();
          }}
        />
        <TemplatesDialog open={templatesOpen} ctx={ctx} onClose={() => setTemplatesOpen(false)} onUsed={() => { setTemplatesOpen(false); reload(); }} />
        <ProjectSettingsDialog
          project={settingsOpen ? project : null}
          ctx={ctx}
          onClose={() => setSettingsOpen(false)}
          onChanged={() => { ctx.reloadProjects(); reload(); }}
          onDeleted={() => { setProjectId(null); ctx.reloadProjects(); }}
        />
        <InviteDialog open={inviteOpen} workspaceId={workspaceId} onClose={() => setInviteOpen(false)} notify={notify} onDone={() => void loadPeople(workspaceId)} />
        <NewProjectDialog
          open={projectOpen}
          workspaceId={workspaceId}
          onClose={() => setProjectOpen(false)}
          notify={notify}
          onCreated={(id) => {
            setNav("project");
            void loadProjects(workspaceId, id);
          }}
        />
      </FluentProvider>
    </div>
  );
};

export default TaskManager;